import { defaultTrialTime, sportTrials } from '../config/trialSettings'
import { useCountdown } from './useCountdown'
import { SportTrial, EventTrial } from '../types/timer'
import { useEffect, useMemo } from 'react'
import useLocalStorage from './useLocalStorage'
import { LocalStorageItems } from '../constant/localStorageItems'
import { log } from 'node:util'

const defaultLimitations = JSON.stringify(sportTrials)

const getSportTrials = (localStorageValue: string): EventTrial[] => {
    let trials
    try {
        trials = JSON.parse(localStorageValue)
    } catch (error) {
        trials = sportTrials
    }
    return trials
}

const getSportTrialTime = (trialTime: number | undefined): number => {
    return typeof trialTime === 'number' ? trialTime : defaultTrialTime
}

export const useSportTrial = (
    currentSport: string,
    enabled = true
): SportTrial => {
    const [value, setValue] = useLocalStorage(
        LocalStorageItems.SPORT_TRIALS,
        defaultLimitations
    )
    const trialsSettings = useMemo(() => getSportTrials(value), [value])
    const currentSportTrial = useMemo(
        () => trialsSettings?.find((trial) => trial.name === currentSport),
        [currentSport, trialsSettings]
    )
    const sportTrialTime = getSportTrialTime(currentSportTrial?.trialTime)
    const { total, resetTimer, ...rest } = useCountdown({
        timeLeft: sportTrialTime,
        enabled,
    })

    const isActive = total > 0

    useEffect(() => {
        let updatedTrials = []
        const sportExists = trialsSettings.some(
            (trial) => trial.name === currentSport
        )

        if (sportExists) {
            updatedTrials = trialsSettings.map((trial) =>
                trial.name === currentSport
                    ? { ...trial, trialTime: total }
                    : trial
            )
        } else {
            updatedTrials = [
                ...trialsSettings,
                { name: currentSport, trialTime: total },
            ]
            setValue(JSON.stringify(updatedTrials))
        }

        setValue(JSON.stringify(updatedTrials))
    }, [total, currentSport])

    useEffect(() => {
        resetTimer(sportTrialTime)
    }, [currentSport])

    return {
        isActive,
        timeLeft: {
            ...rest,
            total,
        },
    }
}
